
export type PlacementTiming = 'exact' | 'estimated';

export interface PlacementMedia {
  itemId: string;
  s3Key: string;
  title: string;
  source: string;
  durationMs: number | null;
  width: number | null;
  height: number | null;
  fps: number | null;
  query: string;
  querySource: string;
  score: number | null;
  branches: string[];
  rank: number | null;
  url: string | null;
  shorterThanWindow: boolean;
  why: string;
}

export interface PlacementItem {
  order: number;
  segmentIndex: number;
  startWord: number;
  endWord: number;
  wordNumber: number;
  anchorText: string;
  where: string;
  startMs: number;
  endMs: number;
  durationMs: number;
  startTc: string;
  endTc: string;
  timing: PlacementTiming;
  timingSource: string | null;
  media: PlacementMedia | null;
  alternatives: PlacementMedia[];
  searchDegraded: boolean;
  searchDegradedReason: string;
  notes: string[];
  weak: boolean;
  weakReason: string;
}

export interface PlacementSkipped {
  segmentIndex: number;
  startWord: number;
  endWord: number;
  wordNumber: number;
  anchorText: string;
  reason: string;
  message: string;
}

export interface PlacementUnmatched {
  segmentIndex: number;
  startWord: number;
  endWord: number;
  wordNumber: number;
  anchorText: string;
  query: string;
  querySource: string;
  reason: string;
  triedQueries: string[];
}

export interface PlacementRejected {
  reason: string;
  message: string;
  fixHint: string;
  segmentIndex: number | null;
  startWord: number | null;
  endWord: number | null;
}

export interface AppliedDensity {
  profile: string;
  minInsertMs: number | null;
  maxInsertMs: number | null;
  minVisibleMs: number | null;
  minGapMs: number | null;
  hookGuardMs: number | null;
  tailGuardMs: number | null;
  allowAdjacent: boolean;
  densityBudget: number | null;
  insertsPerMinute: number | null;
  insertsPer100Words: number | null;
}

export interface PlacementCounts {
  placed: number;
  skipped: number;
  unmatched: number;
  rejected: number;
  total: number;
}

export interface PlacementProposalView {
  proposalId: string;
  language: string;
  timing: PlacementTiming;
  timingNote: string;
  segments: string[];
  totalWords: number;
  durationMs: number;
  durationTc: string;
  density: AppliedDensity;
  items: PlacementItem[];
  skipped: PlacementSkipped[];
  unmatched: PlacementUnmatched[];
  rejected: PlacementRejected[];
  repairReasons: PlacementRejected[];
  repairs: string[];
  warnings: string[];
  createdAt: string | null;
  counts: PlacementCounts;
}

function rec(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function str(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback;
}

function numOrNull(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}

function num(value: unknown, fallback: number): number {
  return numOrNull(value) ?? fallback;
}

function bool(value: unknown): boolean {
  return value === true;
}

function list(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function strings(value: unknown): string[] {
  return list(value)
    .map((item) => (typeof item === 'string' ? item : String(item ?? '')))
    .filter(Boolean);
}

function parseMedia(raw: unknown): PlacementMedia | null {
  const data = rec(raw);
  const itemId = str(data.item_id);
  if (!itemId) return null;
  return {
    itemId,
    s3Key: str(data.s3_key),
    title: str(data.title) || 'Без названия',
    source: str(data.source),
    durationMs: numOrNull(data.duration_ms),
    width: numOrNull(data.width),
    height: numOrNull(data.height),
    fps: numOrNull(data.fps),
    query: str(data.query),
    querySource: str(data.query_source),
    score: numOrNull(data.score),
    branches: strings(data.branches),
    rank: numOrNull(data.rank),
    url: str(data.url) || str(data.preview_url) || null,
    shorterThanWindow: bool(data.shorter_than_window),
    why: str(data.why),
  };
}

function parseItem(raw: unknown, index: number): PlacementItem {
  const data = rec(raw);
  const startMs = num(data.start_ms, 0);
  const endMs = num(data.end_ms, startMs);
  return {
    order: num(data.order, index + 1),
    segmentIndex: num(data.segment_index, 0),
    startWord: num(data.start_word, 0),
    endWord: num(data.end_word, 0),
    wordNumber: num(data.word_number, 0),
    anchorText: str(data.anchor_text),
    where: str(data.where),
    startMs,
    endMs,
    durationMs: num(data.duration_ms, endMs - startMs),
    startTc: str(data.start_tc),
    endTc: str(data.end_tc),
    timing: str(data.timing) === 'exact' ? 'exact' : 'estimated',
    timingSource: str(data.timing_source) || null,
    media: parseMedia(data.media),
    alternatives: list(data.alternatives)
      .map(parseMedia)
      .filter((media): media is PlacementMedia => media !== null),
    searchDegraded: bool(data.search_degraded),
    searchDegradedReason: str(data.search_degraded_reason),
    notes: strings(data.notes),
    weak: bool(data.weak),
    weakReason: str(data.weak_reason),
  };
}

function parseSkipped(raw: unknown): PlacementSkipped {
  const data = rec(raw);
  return {
    segmentIndex: num(data.segment_index, 0),
    startWord: num(data.start_word, 0),
    endWord: num(data.end_word, 0),
    wordNumber: num(data.word_number, 0),
    anchorText: str(data.anchor_text),
    reason: str(data.reason),
    message: str(data.message),
  };
}

function parseUnmatched(raw: unknown): PlacementUnmatched {
  const data = rec(raw);
  return {
    segmentIndex: num(data.segment_index, 0),
    startWord: num(data.start_word, 0),
    endWord: num(data.end_word, 0),
    wordNumber: num(data.word_number, 0),
    anchorText: str(data.anchor_text),
    query: str(data.query),
    querySource: str(data.query_source),
    reason: str(data.reason),
    triedQueries: strings(data.tried_queries),
  };
}

function parseRejected(raw: unknown): PlacementRejected {
  const data = rec(raw);
  return {
    reason: str(data.reason),
    message: str(data.message),
    fixHint: str(data.fix_hint),
    segmentIndex: numOrNull(data.segment_index),
    startWord: numOrNull(data.start_word),
    endWord: numOrNull(data.end_word),
  };
}

function parseDensity(raw: unknown): AppliedDensity {
  const data = rec(raw);
  return {
    profile: str(data.profile, 'default'),
    minInsertMs: numOrNull(data.min_insert_ms),
    maxInsertMs: numOrNull(data.max_insert_ms),
    minVisibleMs: numOrNull(data.min_visible_ms),
    minGapMs: numOrNull(data.min_gap_ms),
    hookGuardMs: numOrNull(data.hook_guard_ms),
    tailGuardMs: numOrNull(data.tail_guard_ms),
    allowAdjacent: bool(data.allow_adjacent),
    densityBudget: numOrNull(data.density_budget),
    insertsPerMinute: numOrNull(data.inserts_per_minute),
    insertsPer100Words: numOrNull(data.inserts_per_100_words),
  };
}

export function parseProposal(raw: unknown): PlacementProposalView | null {
  const data = rec(raw);
  const proposalId = str(data.proposal_id);
  if (!proposalId) return null;

  const items = list(data.items).map(parseItem);
  const skipped = list(data.skipped).map(parseSkipped);
  const unmatched = list(data.unmatched).map(parseUnmatched);
  const rejected = list(data.rejected).map(parseRejected);
  const counts = rec(data.counts);

  return {
    proposalId,
    language: str(data.language, 'ru'),
    timing: str(data.timing) === 'exact' ? 'exact' : 'estimated',
    timingNote: str(data.timing_note),
    segments: strings(data.segments),
    totalWords: num(data.total_words, 0),
    durationMs: num(data.duration_ms, 0),
    durationTc: str(data.duration_tc),
    density: parseDensity(data.density),
    items,
    skipped,
    unmatched,
    rejected,
    repairReasons: list(data.repair_reasons).map(parseRejected),
    repairs: strings(data.repairs),
    warnings: strings(data.warnings),
    createdAt: str(data.created_at) || null,
    counts: {
      placed: num(counts.placed, items.length),
      skipped: num(counts.skipped, skipped.length),
      unmatched: num(counts.unmatched, unmatched.length),
      rejected: num(counts.rejected, rejected.length),
      total: num(
        counts.total,
        items.length + skipped.length + unmatched.length + rejected.length,
      ),
    },
  };
}
